import { companySiteLabel } from "./companyBrand";
import { richTextToHtml } from "./richText";
import { isInstructorWritten } from "./lessonAuthorship";
import { LESSON_SECTION_ORDER, sectionLabel } from "./lessonSections";

/**
 * Inline styles for rich-text blocks inside the PDF's dark surface. Inline
 * because the markup is injected as a detached subtree on web (see the
 * scoping note on the <style> block below) — a stylesheet rule for `ul`
 * would never reach it.
 */
const richStyles = (color) => ({
  paragraph: `color: ${color}; font-size: 14px; line-height: 1.7; margin: 0 0 8px 0;`,
  list: `color: ${color}; font-size: 14px; line-height: 1.7; margin: 0 0 8px 0; padding-left: 20px;`,
  item: "margin-bottom: 4px;",
});

/**
 * Build an HTML string for rendering a lesson summary as a PDF.
 *
 * Used by both the web frontend (via html2pdf.js) and the mobile app
 * (via expo-print). Uses inline CSS only for compatibility with both
 * renderers.
 *
 * @param {Object} params
 * @param {Object} params.lesson - Lesson data object.
 * @param {string} params.studentName - Student display name.
 * @param {string} [params.teacherName] - Teacher name (shown on student exports).
 * @returns {string} Complete HTML document string.
 */
export function buildLessonPdfHtml({ lesson, studentName, teacherName }) {
  const esc = (str) =>
    String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const title = esc(lesson.title || "Music Lesson");
  // Rendered rather than escaped-and-dumped: the exported PDF has to show the
  // same bullets and line breaks the instructor typed. richTextToHtml escapes
  // every text run itself, so it takes the RAW stored value.
  const summary = richTextToHtml(lesson.summary, richStyles("#a1a1aa"));

  const date = new Date(lesson.created_at).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const totalSeconds = lesson.duration_seconds || 0;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const duration = minutes === 0
    ? `${seconds}s`
    : seconds
      ? `${minutes}m ${seconds}s`
      : `${minutes}m`;

  const meta = teacherName
    ? `Lesson with ${esc(teacherName)}`
    : `Student: ${esc(studentName)}`;

  // Program attribution (Spec 12) — the exported lesson carries the same
  // "Part of {program} · {org}" label as the on-screen chip, so a PDF that
  // leaves Hans still says which program the lesson belonged to.
  // org_accent is org-admin-controlled and interpolated raw into a style="…",
  // so it is only trusted as a bare hex (same rule as the email builder).
  const accent = /^#[0-9a-fA-F]{6}$/.test(lesson.org_accent || "") ? lesson.org_accent : "#f4cf08";
  const programHtml = lesson.program_title
    ? `
    <div style="margin-top: 8px; font-size: 12px; color: #a1a1aa;">
      <span style="display: inline-block; width: 7px; height: 7px; border-radius: 4px;
                   background: ${accent}; margin-right: 6px;"></span>Part of ${esc(lesson.program_title)}${
        lesson.org_name ? `&nbsp;&#183;&nbsp;${esc(lesson.org_name)}` : ""
      }
    </div>`
    : "";

  const font = `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;

  const sectionTitle = (label) => `
    <div style="font-size: 11px; font-weight: 600; letter-spacing: 2px;
                text-transform: uppercase; color: #f4cf08; margin-bottom: 12px;">
      ${label}
      <div style="margin-top: 4px; width: 28px; height: 2px; background: #f4cf08;"></div>
    </div>`;

  // Who is reading this copy. Only a student export passes teacherName —
  // the instructor page exports with a studentName alone — so the existing
  // argument already carries the audience and no new parameter is needed.
  const audience = teacherName ? "family" : "instructor";
  const heading = (key) => sectionTitle(esc(sectionLabel(key, audience)));

  // Numbered gold circles — Student Focus took this treatment over from the
  // retired Key Elements, so a reader's eye lands where it always did.
  const numberedSection = (key, items) => {
    if (!items?.length) return "";
    const rows = items
      .map(
        (item, i) => `
      <div style="display: flex; align-items: flex-start; margin-bottom: 8px;">
        <div style="width: 22px; height: 22px; border-radius: 11px; background: #f4cf08;
                    color: #000; font-size: 11px; font-weight: 700; text-align: center;
                    line-height: 22px; flex-shrink: 0; margin-right: 10px;">${i + 1}</div>
        <span style="color: #e4e4e7; font-size: 14px; line-height: 1.5;">${esc(item)}</span>
      </div>`
      )
      .join("");
    return `
      <div style="margin-bottom: 24px; page-break-inside: avoid;">
        ${heading(key)}
        ${rows}
      </div>`;
  };

  const studentFocusHtml = numberedSection("student_focus", lesson.student_focus);

  // Key Elements — RETIRED, still rendered for lessons that carry it.
  const keyElementsHtml = numberedSection("key_elements", lesson.key_elements);

  // Practice Items
  let practiceItemsHtml = "";
  if (lesson.practice_items?.length) {
    const items = lesson.practice_items
      .map(
        (item) => `
      <div style="display: flex; align-items: flex-start; margin-bottom: 6px;">
        <span style="color: #f4cf08; margin-right: 8px; flex-shrink: 0;">&#10003;</span>
        <span style="color: #fafafa; font-size: 14px; line-height: 1.5;">${esc(item)}</span>
      </div>`
      )
      .join("");
    practiceItemsHtml = `
      <div style="margin-bottom: 24px; padding: 16px; background: rgba(244,207,8,0.06);
                  border-left: 3px solid #f4cf08; border-radius: 8px; page-break-inside: avoid;">
        ${heading("practice_items")}
        ${items}
      </div>`;
  }

  // Core Concepts
  let coreConceptsHtml = "";
  if (lesson.core_concepts?.length) {
    const pills = lesson.core_concepts
      .map(
        (c) => `
      <span style="display: inline-block; margin: 0 6px 6px 0; padding: 4px 12px;
                   background: #27272a; color: #a1a1aa; font-size: 12px;
                   border-radius: 12px;">${esc(c)}</span>`
      )
      .join("");
    coreConceptsHtml = `
      <div style="margin-bottom: 24px; page-break-inside: avoid;">
        ${heading("core_concepts")}
        <div>${pills}</div>
      </div>`;
  }

  // Student Requests
  let studentRequestsHtml = "";
  if (lesson.student_requests?.length) {
    const items = lesson.student_requests
      .map(
        (item) => `
      <div style="display: flex; align-items: flex-start; margin-bottom: 6px;">
        <span style="color: #10b981; margin-right: 8px; flex-shrink: 0;">&#8226;</span>
        <span style="color: #a1a1aa; font-size: 14px; line-height: 1.5;">${esc(item)}</span>
      </div>`
      )
      .join("");
    studentRequestsHtml = `
      <div style="margin-bottom: 24px; padding: 16px; background: rgba(16,185,129,0.06);
                  border-left: 3px solid #10b981; border-radius: 8px; page-break-inside: avoid;">
        ${heading("student_requests")}
        ${items}
      </div>`;
  }

  // Instructor Notes
  let instructorNotesHtml = "";
  if (lesson.instructor_notes) {
    instructorNotesHtml = `
      <div style="margin-bottom: 24px; padding: 16px; background: #18181b;
                  border: 1px solid #27272a; border-radius: 8px; page-break-inside: avoid;
                  font-style: italic;">
        ${heading("instructor_notes")}
        ${richTextToHtml(lesson.instructor_notes, richStyles("#a1a1aa"))}
      </div>`;
  }

  // Which sections appear, and in what order, is one decision made once in
  // lib/lessonSections — not re-typed here. An empty section contributes an
  // empty string and disappears, which is what lets the retired keys sit in
  // the order without affecting a lesson that has nothing for them.
  const summaryHtml = summary
    ? `
  <div style="margin-bottom: 24px; page-break-inside: avoid;">
    ${heading("summary")}
    <div style="border-left: 3px solid #f4cf08; padding: 12px 16px;">
      ${summary}
    </div>
  </div>`
    : "";
  const sectionBlocks = {
    summary: summaryHtml,
    student_focus: studentFocusHtml,
    key_elements: keyElementsHtml,
    practice_items: practiceItemsHtml,
    instructor_notes: instructorNotesHtml,
    core_concepts: coreConceptsHtml,
    student_requests: studentRequestsHtml,
  };
  const sectionsHtml = LESSON_SECTION_ORDER.map((key) => sectionBlocks[key] || "").join("");

  // Authorship line — a PDF is the copy most likely to be forwarded or
  // printed, so it has to carry the same provenance claim as the email.
  const authorship = isInstructorWritten(lesson)
    ? `Written by ${esc(teacherName || "the instructor")} — not an AI summary`
    : "Summarized by Hans";

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${title}</title>
<style>
  /* Scoped to the root: web injects this markup with innerHTML into a plain
     <div>, which drops <body> but keeps this <style>. An unscoped reset would
     leak into the host app's stylesheet for the lifetime of the export. */
  #hans-lesson-pdf, #hans-lesson-pdf * { margin: 0; padding: 0; box-sizing: border-box; }
  #hans-lesson-pdf { font-family: ${font}; background: #09090b; color: #fafafa; }
  @media print { #hans-lesson-pdf { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>
<!-- The surface colors live on this div, not on <body>: web renders this
     subtree detached from any <body>, so a body rule would never apply and
     the near-white text would come out white-on-white. -->
<div id="hans-lesson-pdf" style="background: #09090b; color: #fafafa; font-family: ${font};">
<div style="max-width: 640px; margin: 0 auto; padding: 32px 24px;">

  <!-- Header -->
  <div style="text-align: center; margin-bottom: 32px; padding: 28px 20px;
              background: #18181b; border-radius: 12px;">
    <div style="font-size: 22px; font-weight: 500; color: #ffffff; letter-spacing: -0.025em;">
      Lesson Summary
    </div>
  </div>

  <!-- Title & Meta -->
  <div style="margin-bottom: 24px; padding-bottom: 20px; border-bottom: 1px solid #27272a;">
    <h1 style="font-size: 22px; font-weight: 500; color: #ffffff; letter-spacing: -0.025em;
               margin-bottom: 8px;">${title}</h1>
    <div style="font-size: 13px; color: #71717a;">
      ${meta}&nbsp;&nbsp;&#183;&nbsp;&nbsp;${esc(date)}&nbsp;&nbsp;&#183;&nbsp;&nbsp;${duration}
    </div>
    <div style="margin-top: 6px; font-size: 12px; color: #71717a;">${authorship}</div>
    ${programHtml}
  </div>

  ${sectionsHtml}

  <!-- Footer -->
  <div style="margin-top: 32px; padding-top: 20px; border-top: 1px solid #27272a;
              text-align: center;">
    <div style="font-size: 11px; color: #52525b;">Powered by Hans</div>
    <div style="font-size: 11px; color: #f4cf08; margin-top: 4px;">${companySiteLabel()}</div>
  </div>

</div>
</div>
</body>
</html>`;
}

/**
 * Build a sanitized PDF filename from a lesson title and student name.
 * @param {string} title - Raw lesson title.
 * @param {string} [studentName] - Student's display name.
 * @returns {string} Filename-safe string, e.g. "C Major Scales - Alice.pdf"
 */
export function lessonPdfFilename(title, studentName) {
  const sanitize = (str) =>
    String(str || "")
      .replace(/[^a-zA-Z0-9 _-]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const safeTitle = sanitize(title) || "Lesson Summary";
  const safeName = sanitize(studentName);
  const base = safeName ? `${safeTitle} - ${safeName}` : safeTitle;
  return `${base.slice(0, 80)}.pdf`;
}
